/**
 * Icons, drawn inline.
 *
 * SVG in the bundle rather than a font or a package: there are nine of them,
 * an icon font is a network request and a flash of nothing before it lands,
 * and a library is a megabyte to use a handful. These also inherit
 * `currentColor`, which is what lets one set work across four themes without
 * anybody maintaining four copies.
 *
 * `stroke` rather than `fill` throughout, at a consistent 1.8 weight, so they
 * sit together as a set rather than as nine drawings that happen to be
 * nearby.
 */
import type { ReactNode } from 'react';

function Svg({ children, size = 20 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Library — a stack of film. */
export const IconLibrary = (p: { size?: number }) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4" />
  </Svg>
);

/** Live TV — a set with an aerial. */
export const IconLiveTv = (p: { size?: number }) => (
  <Svg {...p}>
    <rect x="2" y="7" width="20" height="13" rx="2" />
    <path d="m7 3 5 4 5-4" />
  </Svg>
);

/** Games — a controller: D-pad left, two buttons right. */
export const IconGames = (p: { size?: number }) => (
  <Svg {...p}>
    <path d="M7 7h10a5 5 0 0 1 4.6 7l-.9 2.2a2.8 2.8 0 0 1-4.6.9L14.3 15H9.7l-1.8 2.1a2.8 2.8 0 0 1-4.6-.9L2.4 14A5 5 0 0 1 7 7Z" />
    <path d="M7.5 9.5v4M5.5 11.5h4" />
    <circle cx="15.5" cy="10.5" r=".6" />
    <circle cx="17.5" cy="12.5" r=".6" />
  </Svg>
);

/** Radio — a receiver with a dial. */
export const IconRadio = (p: { size?: number }) => (
  <Svg {...p}>
    <rect x="2" y="8" width="20" height="12" rx="2" />
    <path d="m17 2-9 6" />
    <circle cx="8" cy="14" r="2.5" />
    <path d="M16 12h3M16 16h3" />
  </Svg>
);

export const IconSearch = (p: { size?: number }) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
);

export const IconAccounts = (p: { size?: number }) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 19a6.5 6.5 0 0 1 13 0" />
    <path d="M17 11.2a3.2 3.2 0 0 0 0-6.4M18 19a6.4 6.4 0 0 0-2-4.6" />
  </Svg>
);

/** Playing now — the one nav item that is about state, not a destination. */
export const IconPlaying = (p: { size?: number }) => (
  <Svg {...p}>
    <path d="M4 16V8M9 19V5M14 17V7M19 14v-4" />
  </Svg>
);

/* ── status, for cards ─────────────────────────────────────────────────────
   Three states somebody actually cares about, and they need to be tellable
   apart at a glance from across a room rather than read. Shape carries the
   meaning and colour reinforces it: a tick is finished, a clock is waiting,
   a half-circle is partway. Colour alone would fail for anyone who cannot
   separate green from amber, which is about one man in twelve. */

export const IconCheck = (p: { size?: number }) => (
  <Svg {...p}><path d="m5 13 4 4L19 7" /></Svg>
);

export const IconClock = (p: { size?: number }) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </Svg>
);

/** Some of it is here and more is coming — a part-filled disc. */
export const IconPartial = (p: { size?: number }) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 3.5a8.5 8.5 0 0 1 0 17z" fill="currentColor" stroke="none" />
  </Svg>
);

/* ── player chrome, for the mobile bottom bar ────────────────────────────
   The bar's own buttons used to be a wall of text ("Full screen", "Exit full
   screen", "Pause") — fine at arm's length on a desk, unreadable and, worse,
   uncrossable on a phone: on a narrow screen the row ran past the edge with
   no way back to whatever it pushed off. These replace the label with a
   picture at that width; the label itself survives as an aria-label rather
   than disappearing, and as visible text everywhere wider. Filled shapes
   (fill=currentColor, stroke=none) rather than the stroked outline the rest
   of the set uses — a stroked triangle and two stroked bars read as a
   diagram at 18px, not a button. */

export const IconPlay = (p: { size?: number }) => (
  <Svg {...p}><path d="M8 5.5v13l11-6.5z" fill="currentColor" stroke="none" /></Svg>
);

export const IconPause = (p: { size?: number }) => (
  <Svg {...p}>
    <rect x="6.5" y="5" width="4" height="14" rx="1" fill="currentColor" stroke="none" />
    <rect x="13.5" y="5" width="4" height="14" rx="1" fill="currentColor" stroke="none" />
  </Svg>
);

export const IconStop = (p: { size?: number }) => (
  <Svg {...p}><rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none" /></Svg>
);

/** Retry — redo, not the reload icon: this repeats one action, not the page. */
export const IconRetry = (p: { size?: number }) => (
  <Svg {...p}>
    <path d="M20 11.5A8 8 0 1 1 17.5 6" />
    <path d="M20 4v6h-6" />
  </Svg>
);

/** Enter fullscreen — four corners pulling outward. */
export const IconExpand = (p: { size?: number }) => (
  <Svg {...p}>
    <path d="M9 3H3v6M15 3h6v6M9 21H3v-6M15 21h6v-6" />
  </Svg>
);

/** Exit fullscreen — the same four corners, pulled back in. */
export const IconCollapse = (p: { size?: number }) => (
  <Svg {...p}>
    <path d="M3 9V3h6M15 3h6v6M3 15v6h6M15 21h6v-6" />
  </Svg>
);

